import { useReactiveVar } from '@apollo/client';
import { ToastRegion } from '@ui-components/neo.tax';
import { ComponentType, ReactElement } from 'react';
import { createPortal } from 'react-dom';

import { useIpWhitelistError, useLinkAccountRedirect, useUserStateReconciliation } from '../hooks';
import { OutdatedVersionModal, AlertModal, useIsVersionOutdatedPoll } from '../organisms';
import { VersionGuardState, ToastConfigState, useAuthenticationService, AlertModalState } from '../state';
import { useLaunchDarklyIdentify, initializeFullStory, initializeSentry } from '../third-parties';
import { isLocalEnvironment } from '../utils';
import { useIdentifyUserToThirdParties } from './hooks/useIdentifyUserToThirdParties';

type ComponentWrapper = (Component: ComponentType<Record<string, unknown>>) => ComponentType<Record<string, unknown>>;

export const initializeThirdParties = (): void => {
  if (!isLocalEnvironment()) {
    initializeFullStory();
  }
  initializeSentry();
};

export const wrapWithInitializationHooks = (
  Component: ComponentType<Record<string, unknown>>,
): ComponentType<Record<string, unknown>> => {
  const WrappedComponent = (props: Record<string, unknown>): ReactElement => {
    useLinkAccountRedirect();
    useAuthenticationService();
    useLaunchDarklyIdentify();
    useUserStateReconciliation();
    useIdentifyUserToThirdParties();
    useIpWhitelistError();
    // TODO Add back or create hook for warmly, depending on sales decision
    // useHubSpotChat();

    return <Component {...props} />;
  };
  // see react/display-name eslint rule
  WrappedComponent.displayName = `InitializationHooksWrapped${Component.displayName ?? Component.name}`;
  return WrappedComponent;
};

export const wrapWithAlertModalProvider = (
  Component: ComponentType<Record<string, unknown>>,
): ComponentType<Record<string, unknown>> => {
  const WrappedComponent = (props: Record<string, unknown>): ReactElement => {
    const alertModalOptions = AlertModalState.useAlertModal();

    return (
      <>
        <Component {...props} />
        {createPortal(<AlertModal isOpen={!!alertModalOptions} {...alertModalOptions} />, document.body)}
      </>
    );
  };
  // see react/display-name eslint rule
  WrappedComponent.displayName = `AlertModalProviderWrapped${Component.displayName ?? Component.name}`;
  return WrappedComponent;
};

export const wrapWithToastProvider = (
  Component: ComponentType<Record<string, unknown>>,
): ComponentType<Record<string, unknown>> => {
  const WrappedComponent = (props: Record<string, unknown>): ReactElement => {
    const toastConfigs = ToastConfigState.useToastConfigs();

    return (
      <>
        <Component {...props} />
        {createPortal(<ToastRegion toastConfigs={toastConfigs} />, document.body)}
      </>
    );
  };
  // see react/display-name eslint rule
  WrappedComponent.displayName = `ToastProviderWrapped${Component.displayName ?? Component.name}`;
  return WrappedComponent;
};

export const wrapWithVersionGuard = (
  Component: ComponentType<Record<string, unknown>>,
): ComponentType<Record<string, unknown>> => {
  const WrappedComponent = (props: Record<string, unknown>): ReactElement => {
    useIsVersionOutdatedPoll();
    const { isVersionOutdated } = useReactiveVar(VersionGuardState.versionGuardVar);

    return (
      <>
        <Component {...props} />
        {isVersionOutdated ? <OutdatedVersionModal /> : null}
      </>
    );
  };
  // see react/display-name eslint rule
  WrappedComponent.displayName = `VersionGuardWrapped${Component.displayName ?? Component.name}`;
  return WrappedComponent;
};

/**
 * Used to wrap a component with a flat API.
 * @param component Functional component to wrap
 * @param wrappers Wrappers to be applies sequentially to component
 */
export const wrap = (
  component: ComponentType<Record<string, unknown>>,
  ...wrappers: ComponentWrapper[]
): ComponentType<Record<string, unknown>> =>
  wrappers.reduce((wrappedComponent, wrapper) => wrapper(wrappedComponent), component);
